<template>
    <div class="images-list">
        <!-- Empty State -->
        <div v-if="!modelValue || modelValue.length === 0" class="empty-state congress-schedule-empty-state px-5">
            <p class="mb-4">{{ lang('congresses::congresses.accommodations.images.empty') }}</p>
            <Button type="button" size="sm" color="brand" variant="inline" @click="addImage">
                <span class="action-icon">
                    <Icon icon="phosphor/regular/image"></Icon>
                </span>
                <span class="action-label-text">
                    {{ lang('congresses::congresses.accommodations.images.add') }}
                </span>
            </Button>
        </div>

        <!-- Images List -->
        <div v-else>
            <Sortable :list="modelValue" item-key="_id" tag="div" class="space-y-2" @end="onReordered">
                <template #item="{ element: image, index }">
                    <div class="grid grid-cols-12 gap-2 items-center px-5">
                        <div class="col-span-1">
                            <icon icon="hero/solid/ellipsis-vertical" class="size-4 text-gray-400 cursor-grab"></icon>
                        </div>
                        <div class="col-span-5">
                            <Field
                                v-model="image.media_id"
                                size="sm"
                                label-position="none"
                                :field="{
                                    name: lang('congresses::congresses.accommodations.images.image'),
                                    slug: `image_${accommodationId}_${image._id}`,
                                    type: 'file',
                                    adminComponent: 'AdminFile',
                                    accept: 'image/*',
                                    required: true,
                                }"
                            />
                        </div>
                        <div class="col-span-5">
                            <Field
                                v-model="image.alt"
                                size="sm"
                                label-position="none"
                                :field="{
                                    name: lang('congresses::congresses.accommodations.images.alt'),
                                    slug: `alt_${accommodationId}_${image._id}`,
                                    type: 'text',
                                    adminComponent: 'AdminText',
                                }"
                            />
                        </div>
                        <div class="col-span-1">
                            <Button type="button" size="sm" color="danger" variant="inline" @click="removeImage(index)">
                                <span class="action-icon">
                                    <Icon icon="phosphor/regular/trash"></Icon>
                                </span>
                            </Button>
                        </div>
                    </div>
                </template>
            </Sortable>

            <div class="accommodation-images-list-after-actions">
                <Button type="button" size="sm" color="brand" variant="inline" @click="addImage">
                    <span class="action-icon">
                        <Icon icon="phosphor/regular/image"></Icon>
                    </span>
                    <span class="action-label-text">
                        {{ lang('congresses::congresses.accommodations.images.add') }}
                    </span>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import Field from '@src/Common/Components/Field.vue';
import type { AccommodationImage } from '../types';

defineProps<{
    accommodationId: string;
}>();

const modelValue = defineModel<AccommodationImage[]>({ default: () => [] });

const addImage = () => {
    if (!modelValue.value) {
        modelValue.value = [];
    }
    modelValue.value = [
        ...modelValue.value,
        {
            _id: uuidv4(),
            media_id: null,
            alt: '',
            sort_order: modelValue.value.length,
        },
    ];
};

const removeImage = (index: number) => {
    modelValue.value = modelValue.value.filter((_, idx) => idx !== index);
};

const onReordered = (event: { oldIndex: number; newIndex: number }) => {
    const items = [...modelValue.value];
    const [movedItem] = items.splice(event.oldIndex, 1);
    items.splice(event.newIndex, 0, movedItem);

    items.forEach((img, idx) => {
        img.sort_order = idx;
    });

    modelValue.value = items;
};
</script>
